"use client";

import { useState, useEffect } from "react";

export function useRect(element: HTMLElement | null): DOMRect | null {
  const [measured, setMeasured] = useState<{ element: HTMLElement; rect: DOMRect } | null>(null);

  useEffect(() => {
    if (!element) {
      setMeasured(null);
      return;
    }

    let rAFId: number;

    const update = () => {
      if (!document.body.contains(element)) {
        setMeasured(null);
        return;
      }
      const next = element.getBoundingClientRect();
      setMeasured((prev) => {
        if (
          prev?.element === element &&
          prev.rect.x === next.x &&
          prev.rect.y === next.y &&
          prev.rect.width === next.width &&
          prev.rect.height === next.height
        ) {
          return prev;
        }
        return { element, rect: next };
      });
    };

    update();

    const resizeObserver = new ResizeObserver(() => {
      cancelAnimationFrame(rAFId);
      rAFId = requestAnimationFrame(update);
    });
    resizeObserver.observe(element);
    resizeObserver.observe(document.body);

    const onScroll = () => {
      cancelAnimationFrame(rAFId);
      rAFId = requestAnimationFrame(update);
    };

    const onResize = () => {
      cancelAnimationFrame(rAFId);
      rAFId = requestAnimationFrame(update);
    };

    window.addEventListener("scroll", onScroll, { capture: true, passive: true });
    window.addEventListener("resize", onResize);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
      cancelAnimationFrame(rAFId);
    };
  }, [element]);

  return measured?.element === element ? measured.rect : null;
}
